import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { formatDateLong } from "../lib/dates";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { NewsItem, NewsListResponse } from "@family/shared";

function displayCategory(cat: string): string {
  return cat
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function NewsPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;
  const [filterCategory, setFilterCategory] = useState<string | null>(null);

  const query = useQuery<NewsListResponse>({
    queryKey: ["news", slug],
    queryFn: () => apiFetch<NewsListResponse>(`/families/${slug}/news`),
    enabled: Boolean(slug),
  });

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  const canWrite = can(primary.role, "post:create");
  const items = query.data?.items ?? [];
  const filtered = filterCategory
    ? items.filter((i) => i.category === filterCategory)
    : items;
  const categories = query.data?.categories ?? [];

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div className="row-between">
          <h1>News</h1>
          {canWrite && (
            <Link to="/news/new" style={{ textDecoration: "none" }}>
              <button>+ New item</button>
            </Link>
          )}
        </div>

        <div className="stack-lg">
          {categories.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "var(--s-2)",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={() => setFilterCategory(null)}
                className={
                  !filterCategory ? "" : "btn-secondary"
                }
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  minHeight: 0,
                }}
              >
                All posts
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilterCategory(c)}
                  className={filterCategory === c ? "" : "btn-secondary"}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    minHeight: 0,
                  }}
                >
                  {displayCategory(c)}
                </button>
              ))}
            </div>
          )}

          <div>
            {query.isLoading && <p className="text-muted">Loading…</p>}
            {query.error && (
              <p className="text-error">
                Error: {(query.error as Error).message}
              </p>
            )}

            {filtered.length === 0 && !query.isLoading && (
              <p className="text-muted">
                No news in this section yet.
                {canWrite && " Click + New item to write the first."}
              </p>
            )}

            <ul
              className="list-unstyled"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(260px, 1fr))",
                gap: "var(--s-4)",
              }}
            >
              {filtered.map((item) => (
                <NewsRow key={item.id} item={item} />
              ))}
            </ul>
          </div>
        </div>
      </main>
    </>
  );
}

function NewsRow({ item }: { item: NewsItem }) {
  return (
    <li
      className="card news-card"
      style={{
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
    >
      <Link
        to={`/news/${item.id}`}
        style={{
          textDecoration: "none",
          color: "var(--c-text)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {item.coverUrl ? (
          <div
            style={{
              aspectRatio: "4 / 3",
              background: "var(--c-bg-alt)",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <img
              src={item.coverUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
        ) : (
          <div
            style={{
              aspectRatio: "4 / 3",
              background:
                "linear-gradient(135deg, var(--c-bg-alt), var(--c-border))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--c-text-subtle)",
              fontSize: "0.85rem",
              flexShrink: 0,
            }}
          >
            {displayCategory(item.category)}
          </div>
        )}
        <div
          style={{
            padding: "var(--s-4)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            flex: 1,
          }}
        >
          <div
            className="text-subtle"
            style={{ fontSize: "0.8rem" }}
          >
            {formatDateLong(item.publishedAt)} · {displayCategory(item.category)}
          </div>
          <h3 style={{ fontSize: "1.05rem", margin: 0, lineHeight: 1.3 }}>
            {item.title}
          </h3>
          {item.summary && (
            <p
              className="text-muted"
              style={{
                margin: 0,
                lineHeight: 1.5,
                fontSize: "0.92rem",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.summary}
            </p>
          )}
          <span
            style={{
              marginTop: "auto",
              fontWeight: 700,
              color: "var(--c-blue)",
              fontSize: "0.88rem",
            }}
          >
            Continue reading →
          </span>
        </div>
      </Link>
    </li>
  );
}
